import { useRef, useState } from 'react';
import axios from 'axios';
import API_BASE from '../../utils/apiBase';

const BLANK = { organisation: '', contact_name: '', email: '', message: '', website: '' };

function makeRequestKey() {
  // A retry identifier, not a credential. Reused after network errors only.
  return `caredock-${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}-${Math.random().toString(36).slice(2)}`;
}

export default function CareDockProspectForm({ noticeUrl, noticeVersion }) {
  const [values, setValues] = useState(BLANK);
  const [consent, setConsent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  const requestKey = useRef(null);

  if (!/^https:\/\//i.test(noticeUrl || '') || !noticeVersion) {
    // Cannot display a collecting form without the approved privacy document.
    return <p role="status">Provider enquiries are not accepting submissions yet.</p>;
  }

  async function submit(event) {
    event.preventDefault();
    if (busy || !consent) return;
    setBusy(true);
    setError('');
    if (!requestKey.current) requestKey.current = makeRequestKey();
    try {
      await axios.post(`${API_BASE}/platform/prospects`, {
        ...values,
        privacy_acknowledged: true,
      }, { headers: { 'X-Idempotency-Key': requestKey.current } });
      setSubmitted(true);
      setValues(BLANK);
    } catch (reason) {
      const status = reason?.response?.status;
      if (status === 503) {
        setError('CareDock provider enquiries are temporarily unavailable. Please try again later.');
      } else if (status === 429) {
        setError('Too many requests have been received. Please try again later.');
      } else {
        setError('We could not confirm your enquiry. Please retry once or return later.');
      }
    } finally {
      setBusy(false);
    }
  }

  if (submitted) {
    return (
      <section data-testid="caredock-prospect-confirmation" role="status" className="rounded-xl border border-[#CBE7F6] bg-[#F3FAFF] p-6">
        <h2 className="text-xl font-semibold text-[#0D274E]">Thank you for your enquiry</h2>
        <p className="mt-2 text-sm leading-6 text-[#51677a]">
          Your message has been received by CareDock. Our team will review it.
        </p>
      </section>
    );
  }

  return (
    <form data-testid="caredock-prospect-form" className="mt-6 space-y-4" onSubmit={submit}>
      <p className="text-sm text-[#51677a]">Tell us about your organisation. Please do not include patient, employee or medical records.</p>
      <label className="block text-sm font-semibold text-[#0D274E]">
        Organisation
        <input required minLength={2} maxLength={160} value={values.organisation}
          onChange={e => setValues(v => ({ ...v, organisation: e.target.value }))}
          className="mt-1 w-full rounded-lg border border-[#CBE7F6] p-3" />
      </label>
      <label className="block text-sm font-semibold text-[#0D274E]">
        Your name
        <input required minLength={2} maxLength={120} value={values.contact_name}
          onChange={e => setValues(v => ({ ...v, contact_name: e.target.value }))}
          className="mt-1 w-full rounded-lg border border-[#CBE7F6] p-3" />
      </label>
      <label className="block text-sm font-semibold text-[#0D274E]">
        Work email
        <input required type="email" value={values.email}
          onChange={e => setValues(v => ({ ...v, email: e.target.value }))}
          className="mt-1 w-full rounded-lg border border-[#CBE7F6] p-3" />
      </label>
      <label className="block text-sm font-semibold text-[#0D274E]">
        What would you like to know?
        <textarea required minLength={15} maxLength={1500} rows={4} value={values.message}
          onChange={e => setValues(v => ({ ...v, message: e.target.value }))}
          className="mt-1 w-full rounded-lg border border-[#CBE7F6] p-3" />
      </label>
      <div className="absolute left-[-10000px]" aria-hidden="true">
        <label htmlFor="caredock-company-website">Leave this field empty</label>
        <input id="caredock-company-website" tabIndex={-1} autoComplete="off"
          value={values.website}
          onChange={e => setValues(v => ({ ...v, website: e.target.value }))} />
      </div>
      <label className="flex items-start gap-3 text-sm text-[#51677a]">
        <input type="checkbox" className="mt-1" checked={consent}
          onChange={e => setConsent(e.target.checked)} required />
        <span>I have read the <a href={noticeUrl} target="_blank" rel="noopener noreferrer"
          className="font-semibold text-[#0077B5] underline">CareDock privacy notice</a> (version {noticeVersion}).
          I agree that CareDock may use these details to respond to my enquiry.</span>
      </label>
      <button type="submit" disabled={busy || !consent}
        className="rounded-full bg-[#0D274E] px-7 py-3 text-sm font-semibold text-white disabled:opacity-50">
        {busy ? 'Sending…' : 'Send enquiry'}
      </button>
      {error && <p role="alert" className="text-sm text-red-700">{error}</p>}
    </form>
  );
}
